import React, { useState } from 'react';
import { Avatar, Box, Button } from '@mui/material';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { formatDateAndTime, formatDateOnly } from '../../../utils/helper';
import { ICoupon } from '../../../utils/entities/interface';
import { ToggleCouponStatus } from '../../../services/coupen';
import { ModalConfirmation } from '../../molecules/ModalConfirmation';
import { ListTable } from '../../molecules/ListTable';

export const CouponList = ({
  coupons,
  onUpdate,
  isLoading,
  headerCells,
  onEdit,
}: {
  coupons: ICoupon[];
  onUpdate: () => void;
  isLoading: boolean;
  headerCells: [string, number][];
  onEdit: (coupon: ICoupon) => void;
}) => {
  const [open, setOpenModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<ICoupon | null>(null);

  const handleClose = () => {
    setOpenModal(false);
    setSelectedCoupon(null);
  };

  const handleConfirm = async () => {
    if (selectedCoupon) {
      try {
        await ToggleCouponStatus(selectedCoupon._id);
        onUpdate();
        handleClose();
      } catch (error) {
        alert(
          error instanceof Error ? error.message : 'Failed to update status',
        );
      }
    }
  };

  const handleOpenModal = (coupon: ICoupon) => {
    setSelectedCoupon(coupon);
    setOpenModal(true);
  };

  const actions = [
    {
      icon: <BorderColorOutlinedIcon />,
      onClick: (coupon: ICoupon) => onEdit(coupon),
    },
  ];

  return (
    <>
      <ListTable
        headerCells={headerCells}
        rows={coupons}
        isLoading={isLoading}
        actions={actions}
        renderCell={(coupon: ICoupon, colIndex: number) => {
          switch (colIndex) {
            case 0:
              return (
                <Box display="flex" justifyContent="center">
                  <Avatar
                    src={coupon.image || undefined}
                    alt={coupon.name}
                    variant="rounded"
                    imgProps={{ style: { objectFit: 'contain' } }}
                    sx={{
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      bgcolor: 'transparent',
                      color: '#8A8D91',
                    }}
                  >
                    {coupon.name?.charAt(0)?.toUpperCase() || '?'}
                  </Avatar>
                </Box>
              );
            case 1:
              return coupon.couponCode || 'N/A';
            case 2:
              return coupon.name;
            case 3:
              return coupon.discount + '%' || 'N/A';
            case 4:
              return `₹${coupon.minValue ?? 0}`;
            case 5:
              return (coupon.description || []).join(', ') || 'N/A';
            case 6:
              return formatDateOnly(coupon.expiryDate);
            case 7:
              return formatDateAndTime(coupon.updatedAt);
            case 8:
              return (
                <Button
                  variant="outlined"
                  size="small"
                  color={coupon.isActive ? 'error' : 'success'}
                  onClick={() => handleOpenModal(coupon)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                  }}
                >
                  {coupon.isActive ? 'Disable' : 'Enable'}
                </Button>
              );
            default:
              return null;
          }
        }}
      />

      <ModalConfirmation
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        selectedItem={selectedCoupon}
        entityName="coupon"
      />
    </>
  );
};
